import {
  AemConfiguration,
  BuyDeliveryAddress,
  CreditCard,
  CustomerAddresses,
  CustomerCountry,
  CustomerCreateRequestData,
  CustomerExistsResponse,
  CustomerExploreMoreCard,
  CustomerGender,
  CustomerLanguage,
  CustomerPreferences,
  CustomerProfile,
  CustomerTitle,
  CustomerVoucher,
  DeliveryAddress,
  HTTPStatusCode,
  MultiFasciaPreferences,
  PreferenceListItem,
  Preferences,
} from '@as-react/common-types';
import { axiosClient } from '@as-react/common-utils';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { customerMutationKeys, customerQueryKeys } from '../..';
import { QueryApiOptions, UrlMapping } from '../../types/types';
import { createUrl } from '../../utils/createUrl';

export type CustomerTitlesParams = {
  lang: AemConfiguration['lang'];
};

export type CustomerGendersParams = {
  lang: AemConfiguration['lang'];
};

export type CustomerPreferencesNewsletterParams = {
  customerUUID: string;
  ext_id?: string;
};

export type CustomerEmailExistsParams = {
  email: string;
  sendEmail?: boolean;
};

export type CustomerUpdateEmailParams = {
  email: string;
  customerUUID?: string;
};

export type CustomerSendPasswordResetEmailParams = {
  email: string;
};

export type CustomerCreateAccountParams = {
  customerData: CustomerCreateRequestData;
};

export type UpdatePasswordParams = {
  newPassword: string;
  oldPassword: string;
};

const urlMapping: UrlMapping<typeof customerQueryKeys & typeof customerMutationKeys> = {
  languages: {
    server: 'masterdata/language/all',
    client: 'api/aem/general/language/all',
  },
  titles: {
    server: 'masterdata/title/all',
    client: 'api/aem/general/title/all',
  },
  genders: {
    server: 'masterdata/gender/all',
    client: 'api/aem/general/gender/all',
  },
  countries: {
    server: 'masterdata/country/all',
    client: 'api/aem/general/country/available',
  },
  preferencesNewsletter: {
    client: 'api/customer/preference/newsletter',
  },
  emailExists: {
    client: 'api/customer/exists',
  },
  updateEmail: {
    client: 'api/customer/profile/update/email',
  },
  sendPasswordResetEmail: {
    client: 'api/credentials/forgot',
  },
  profile: {
    client: 'api/customer/profile/details',
  },
  createAccount: {
    client: 'api/customer/profile/create',
  },
  addDeliveryAddress: {
    client: 'api/customer/deliveryaddress/add',
  },
  deleteDeliveryAddress: {
    client: 'api/customer/deliveryaddress/delete',
  },
  updateDeliveryAddress: {
    client: 'api/customer/deliveryaddress/updateaddress',
  },
  getDeliveryAddresses: {
    client: 'api/customer/deliveryaddress',
  },
  updatePhoneNumber: {
    client: 'api/customer/profile/update/mobilephone',
  },
  cardNumberExists: {
    client: 'api/customer/exists/cardnumber',
  },
  updateProfile: {
    client: 'api/customer/profile/update',
  },
  subscribeNewsletter: {
    client: 'api/customer/newsletter/subscribe',
  },
  getPreferences: { client: 'api/customer/preference/v2' },
  getAllPreferences: { client: 'api/customer/preference/all' },
  updatePreferences: { client: 'api/customer/preference/update' },
  updateAllPreferences: { client: 'api/customer/preference/update/all' },
  updatePassword: { client: 'api/v2/customer/password' },
  getCardInfo: { client: 'api/customer/cardalias' },
  deleteCard: { client: 'api/customer/cardalias/{cardId}' },
  getVouchers: { client: 'api/customer/payback' },
};

const processTitleData = (
  data: [{ idTitle: number; titleNameTranslations: []; sortOrder: number; idGender: string }],
  lang: string
): Array<CustomerTitle> => {
  const titles: Array<CustomerTitle> = [];
  data.forEach(title => {
    if (title.titleNameTranslations) {
      const customerTitle: CustomerTitle = {
        id: title.idTitle,
        title: title.titleNameTranslations[lang.toUpperCase()],
        sortOrder: title.sortOrder,
        idGender: title.idGender,
      };
      titles.push(customerTitle);
    }
  });

  return titles;
};

const processGenderData = (
  data: [{ idGenderPeople: number; genderPeopleCode: string; genderTitleTranslations: [] }],
  lang: string
): Array<CustomerGender> => {
  const genders: Array<CustomerGender> = [];

  data.forEach(function (gender) {
    if (gender.genderTitleTranslations) {
      const customerGender: CustomerGender = {
        id: gender.idGenderPeople,
        peopleCode: gender.genderPeopleCode,
        gender: gender.genderTitleTranslations[lang.toUpperCase()],
      };
      genders.push(customerGender);
    }
  });

  return genders;
};

export const getLanguages = async ({
  servicesEndpoint,
  isServer,
  defaultRequestParams,
}: QueryApiOptions): Promise<Array<CustomerLanguage>> => {
  const url = createUrl(servicesEndpoint, urlMapping.languages, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getTitles = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { lang }: CustomerTitlesParams
): Promise<Array<CustomerTitle>> => {
  const url = createUrl(servicesEndpoint, urlMapping.titles, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return processTitleData(result.data, lang);
};

export const getGenders = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { lang }: CustomerGendersParams
): Promise<Array<CustomerGender>> => {
  const url = createUrl(servicesEndpoint, urlMapping.genders, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return processGenderData(result.data, lang);
};

export const getCountries = async ({
  servicesEndpoint,
  isServer,
  defaultRequestParams,
}: QueryApiOptions): Promise<Array<CustomerCountry>> => {
  const url = createUrl(servicesEndpoint, urlMapping.countries, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  const result = await axiosClient.request<Array<CustomerCountry>>(axiosConfig);
  return result.data;
};

export const getPreferencesNewsletter = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { customerUUID, ext_id }: CustomerPreferencesNewsletterParams
): Promise<Record<string, boolean>> => {
  const url = createUrl(servicesEndpoint, urlMapping.preferencesNewsletter, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    withCredentials: true,
    params: {
      ...defaultRequestParams,
      customerUUID,
      ext_id,
    },
  };

  const result = await axiosClient.request<Record<string, boolean>>(axiosConfig);
  return result.data;
};

export const getEmailExists = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { email, sendEmail = false }: CustomerEmailExistsParams
) => {
  const url = createUrl(servicesEndpoint, urlMapping.emailExists, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    withCredentials: true,
    params: {
      ...defaultRequestParams,
      email,
      sendEmail,
    },
  };

  const result = await axiosClient.request<CustomerExistsResponse>(axiosConfig);
  return result.data;
};

export const updateEmail = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { email, customerUUID }: CustomerUpdateEmailParams
) => {
  const url = createUrl(servicesEndpoint, urlMapping.updateEmail, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    withCredentials: true,
    params: {
      ...defaultRequestParams,
      email,
      customerUUID,
    },
  };

  const result = await axiosClient.request<undefined>(axiosConfig);
  return result.data;
};

export const sendPasswordResetEmail = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { email }: CustomerSendPasswordResetEmailParams
) => {
  const url = createUrl(servicesEndpoint, urlMapping.sendPasswordResetEmail, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    url,
    withCredentials: true,
    params: defaultRequestParams,
    data: email,
  };

  const result = await axiosClient.request<undefined>(axiosConfig);
  return result.data;
};

export const getProfile = async ({
  servicesEndpoint,
  isServer,
  defaultRequestParams,
}: QueryApiOptions): Promise<CustomerProfile> => {
  const url = createUrl(servicesEndpoint, urlMapping.profile, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    withCredentials: true,
    params: defaultRequestParams,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const createAccount = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { customerData }: CustomerCreateAccountParams
): Promise<boolean> => {
  const url = createUrl(servicesEndpoint, urlMapping.createAccount, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: defaultRequestParams,
    data: customerData,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const addDeliveryAddress = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  address: BuyDeliveryAddress
): Promise<BuyDeliveryAddress> => {
  const url = createUrl(servicesEndpoint, urlMapping.addDeliveryAddress, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: defaultRequestParams,
    data: address,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const deleteDeliveryAddress = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  address: DeliveryAddress
): Promise<DeliveryAddress> => {
  const url = createUrl(servicesEndpoint, urlMapping.deleteDeliveryAddress, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: defaultRequestParams,
    data: address,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const updateDeliveryAddress = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  address: BuyDeliveryAddress
): Promise<BuyDeliveryAddress> => {
  const url = createUrl(servicesEndpoint, urlMapping.updateDeliveryAddress, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: defaultRequestParams,
    data: address,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getDeliveryAddresses = async ({
  servicesEndpoint,
  isServer,
  defaultRequestParams,
}: QueryApiOptions): Promise<CustomerAddresses> => {
  const url = createUrl(servicesEndpoint, urlMapping.getDeliveryAddresses, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: defaultRequestParams,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const result = await axiosClient.request<CustomerAddresses>(axiosConfig);
  return result.data;
};

export const updatePhoneNumber = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  phoneNumber: string
): Promise<AxiosResponse> => {
  const url = createUrl(servicesEndpoint, urlMapping.updatePhoneNumber, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    headers: { 'Content-Type': 'text/plain' },
    withCredentials: true,
    params: { mainWebshop: defaultRequestParams.mainWebshop },
    data: phoneNumber,
  };

  return await axiosClient.request(axiosConfig);
};

export const getCardNumberExists = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  cardNumber: string
): Promise<CustomerExploreMoreCard> => {
  const url = createUrl(servicesEndpoint, urlMapping.cardNumberExists, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: {
      ...defaultRequestParams,
      cardNumber,
    },
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const updateProfile = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  profile: CustomerProfile
): Promise<CustomerProfile> => {
  const url = createUrl(servicesEndpoint, urlMapping.updateProfile, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    withCredentials: true,
    params: { ...defaultRequestParams },
    data: profile,
  };
  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const subscribeToNewsletter = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  email: string
) => {
  const url = createUrl(servicesEndpoint, urlMapping.subscribeNewsletter, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: {
      ...defaultRequestParams,
      email,
    },
    withCredentials: true,
  };

  const customerEndpointRequest = await axiosClient.request(axiosConfig);
  return customerEndpointRequest.data;
};

export async function getPreferences({
  servicesEndpoint,
  isServer,
  defaultRequestParams,
}: QueryApiOptions): Promise<Array<PreferenceListItem>> {
  const url = createUrl(servicesEndpoint, urlMapping.getPreferences, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    withCredentials: true,
    params: defaultRequestParams,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
}

export async function getMultiFasciaPreferences({
  servicesEndpoint,
  isServer,
  defaultRequestParams,
}: QueryApiOptions): Promise<MultiFasciaPreferences> {
  const url = createUrl(servicesEndpoint, urlMapping.getAllPreferences, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    withCredentials: true,
    params: defaultRequestParams,
  };

  const result = await axiosClient.request<MultiFasciaPreferences>(axiosConfig);

  return result.data;
}

export async function updatePreferences(
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  preferences: Partial<Preferences>,
  email?: string
): Promise<CustomerPreferences> {
  const url = createUrl(servicesEndpoint, urlMapping.updatePreferences, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: {
      ...defaultRequestParams,
      email,
    },
    data: preferences,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
}

export async function updateMultiFasciaPreferences(
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  preferences: MultiFasciaPreferences
): Promise<MultiFasciaPreferences> {
  const url = createUrl(servicesEndpoint, urlMapping.updateAllPreferences, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    withCredentials: true,
    params: defaultRequestParams,
    data: preferences,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
}

export async function updatePassword(
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  passwords: UpdatePasswordParams
): Promise<void> {
  const url = createUrl(servicesEndpoint, urlMapping.updatePassword, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    params: defaultRequestParams,
    data: passwords,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
}

export const getCardInfo = async ({
  servicesEndpoint,
  isServer,
  defaultRequestParams,
}: QueryApiOptions): Promise<Array<CreditCard>> => {
  const url = createUrl(servicesEndpoint, urlMapping.getCardInfo, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  const result = await axiosClient.request<Array<CreditCard>>(axiosConfig);
  return result.data;
};

export const deleteCard = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  cardId: string
): Promise<Array<CreditCard>> => {
  const url = createUrl(servicesEndpoint, urlMapping.deleteCard, isServer, { cardId });
  const axiosConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  const result = await axiosClient.request<Array<CreditCard>>(axiosConfig);
  return result.data;
};

export const getVouchers = async ({
  servicesEndpoint,
  isServer,
  defaultRequestParams,
}: QueryApiOptions): Promise<Array<CustomerVoucher>> => {
  const url = createUrl(servicesEndpoint, urlMapping.getVouchers, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: defaultRequestParams,
    withCredentials: true,
  };

  const result = await axiosClient.request<Array<CustomerVoucher>>(axiosConfig);
  return result.status === HTTPStatusCode.NoContent ? [] : result.data;
};
